function safeParseJson(value: string | null): Record<string, unknown> | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

export interface DiffField {
  key: string;
  before: unknown;
  after: unknown;
  changed: boolean;
}

/** Field-level diff of two JSON snapshots, for entries where before/after are row snapshots. */
export function diffFields(before: string | null, after: string | null): DiffField[] {
  const beforeObj = safeParseJson(before) ?? {};
  const afterObj = safeParseJson(after) ?? {};
  const keys = Array.from(new Set([...Object.keys(beforeObj), ...Object.keys(afterObj)]));
  return keys.map((key) => ({
    key,
    before: beforeObj[key],
    after: afterObj[key],
    changed: JSON.stringify(beforeObj[key]) !== JSON.stringify(afterObj[key]),
  }));
}

function formatValue(value: unknown): string {
  if (value === undefined || value === null) return "—";
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

/** One-line human summary of a change, for CSV/PDF export and collapsed list rows. */
export function summarizeChange(before: string | null, after: string | null): string {
  const fields = diffFields(before, after).filter((f) => f.changed);
  if (fields.length === 0) return "—";
  return fields
    .map((f) => `${f.key}: ${formatValue(f.before)} → ${formatValue(f.after)}`)
    .join("; ");
}

export { safeParseJson, formatValue };

export interface StringArrayDiff {
  added: string[];
  removed: string[];
}

/**
 * Set difference in both directions, de-duplicated and in each side's own order.
 * Drives the per-source add/remove fan-out, where every added or removed id becomes
 * its own audit entry rather than one opaque before/after blob.
 */
export function diffStringArrays(before: string[] | null, after: string[] | null): StringArrayDiff {
  const beforeIds = before ?? [];
  const afterIds = after ?? [];
  const beforeSet = new Set(beforeIds);
  const afterSet = new Set(afterIds);
  return {
    added: Array.from(new Set(afterIds)).filter((id) => !beforeSet.has(id)),
    removed: Array.from(new Set(beforeIds)).filter((id) => !afterSet.has(id)),
  };
}

export interface DiffLine {
  text: string;
  changed: boolean;
}

export interface LineDiff {
  before: DiffLine[];
  after: DiffLine[];
}

/**
 * Cap on the line-matching table. A report section is prose — a few hundred lines at
 * most — so this is never reached in practice; past it the exact match is abandoned and
 * every line in the changed region is highlighted, which is degraded but still correct.
 */
const MAX_MATCH_CELLS = 250_000;

function splitLines(value: string | null): string[] {
  const text = value ?? "";
  return text.length === 0 ? [] : text.split("\n");
}

/**
 * Longest-common-subsequence match of two line arrays, returning which lines on each
 * side are unmatched. Hand-rolled because the project has no diff dependency and this
 * is the only place one would be used.
 */
function matchLines(before: string[], after: string[]) {
  const beforeChanged = before.map(() => true);
  const afterChanged = after.map(() => true);
  if (before.length === 0 || after.length === 0) return { beforeChanged, afterChanged };
  if (before.length * after.length > MAX_MATCH_CELLS) return { beforeChanged, afterChanged };

  // table[i][j] = length of the LCS of before[i…] and after[j…], flattened.
  const width = after.length + 1;
  const table = new Uint32Array((before.length + 1) * width);
  for (let i = before.length - 1; i >= 0; i--) {
    for (let j = after.length - 1; j >= 0; j--) {
      table[i * width + j] =
        before[i] === after[j]
          ? table[(i + 1) * width + j + 1] + 1
          : Math.max(table[(i + 1) * width + j], table[i * width + j + 1]);
    }
  }

  let i = 0;
  let j = 0;
  while (i < before.length && j < after.length) {
    if (before[i] === after[j]) {
      beforeChanged[i] = false;
      afterChanged[j] = false;
      i++;
      j++;
    } else if (table[(i + 1) * width + j] >= table[i * width + j + 1]) {
      i++;
    } else {
      j++;
    }
  }
  return { beforeChanged, afterChanged };
}

const unchangedLine = (text: string): DiffLine => ({ text, changed: false });

/**
 * Line-level diff of two blocks of prose, for rendering a section body's before/after
 * side by side with only the changed lines highlighted. Identical leading and trailing
 * lines are matched off first, so an edit deep in a long section stays cheap.
 */
export function diffLines(before: string | null, after: string | null): LineDiff {
  const beforeLines = splitLines(before);
  const afterLines = splitLines(after);

  let prefix = 0;
  while (
    prefix < beforeLines.length &&
    prefix < afterLines.length &&
    beforeLines[prefix] === afterLines[prefix]
  ) {
    prefix++;
  }

  let suffix = 0;
  while (
    suffix < beforeLines.length - prefix &&
    suffix < afterLines.length - prefix &&
    beforeLines[beforeLines.length - 1 - suffix] === afterLines[afterLines.length - 1 - suffix]
  ) {
    suffix++;
  }

  const middleBefore = beforeLines.slice(prefix, beforeLines.length - suffix);
  const middleAfter = afterLines.slice(prefix, afterLines.length - suffix);
  const { beforeChanged, afterChanged } = matchLines(middleBefore, middleAfter);

  return {
    before: [
      ...beforeLines.slice(0, prefix).map(unchangedLine),
      ...middleBefore.map((text, index) => ({ text, changed: beforeChanged[index] })),
      ...beforeLines.slice(beforeLines.length - suffix).map(unchangedLine),
    ],
    after: [
      ...afterLines.slice(0, prefix).map(unchangedLine),
      ...middleAfter.map((text, index) => ({ text, changed: afterChanged[index] })),
      ...afterLines.slice(afterLines.length - suffix).map(unchangedLine),
    ],
  };
}
